export interface WorkflowFact {
  id: string
  /**
   * What to label the fact with. Nebula sends one for every schema fact — the
   * workflow's own `title`, or a readable form of the id — and `defaultFactTitle`
   * covers the cases it can't reach: the legacy text column, and any response
   * predating the field.
   */
  title: string
  required: boolean
  description: string
  /**
   * The value used when the fact isn't supplied, when the source declares one.
   * Absent for facts parsed from the text column, which carries no defaults, and
   * for schema facts whose `default` is null — Nebula spells "no default" that
   * way, and it is not worth rendering.
   *
   * Always absent for a `secret` fact, whichever source it came from: see
   * `factsFromSchema`.
   */
  default?: string | number | boolean
  /**
   * The fact holds a credential, so its value is never rendered.
   *
   * Carried onto the render model purely so the omitted `default` reads as
   * deliberate — a row that showed neither a value nor a reason would look like
   * a fact that simply declares no default. Facts from the legacy text column
   * carry no secret marker and leave this undefined.
   */
  secret?: boolean
}

// Current format: one fact per line, `fact_id [required|optional] — description`.
const LINE_PATTERN =
  /^([A-Za-z_][A-Za-z0-9_]*)\s*(?:\[(required|optional)\])?\s*—\s*(.*)$/

// Legacy fallback: a single paragraph of `fact_id — description.` entries with
// no required/optional markers.
const PARAGRAPH_PATTERN =
  /([A-Za-z_][A-Za-z0-9_]*)\s+—\s+(.*?)(?=\s+[A-Za-z_][A-Za-z0-9_]*\s+—\s|$)/gs

const MARKER_PATTERN = /\[(required|optional)\]/

/**
 * A readable name for a fact with no declared title: `note_body` → `Note Body`.
 *
 * Mirrors Nebula's `default_fact_title`, deliberately — the two can't share
 * code, and a fact must still be labelled when the title never arrives (the
 * legacy text column carries no schema at all). Only the first letter of each
 * word is touched, so an id that already spells an initialism keeps it.
 */
export function defaultFactTitle(id: string): string {
  const words = id
    .trim()
    .split(/[_\-\s]+/)
    .filter(Boolean)
  if (words.length === 0) return id
  return words.map((word) => word[0]!.toUpperCase() + word.slice(1)).join(' ')
}

/** The label for a fact: what the schema declared, else derived from the id. */
export function factTitle(id: string, title?: string | null): string {
  return title?.trim() || defaultFactTitle(id)
}

// Required facts first, then alphabetically by title — the list is read by
// title, so ordering it by the hidden id would look arbitrary.
function sortFacts(facts: WorkflowFact[]): WorkflowFact[] {
  return facts.toSorted((a, b) => {
    if (a.required !== b.required) return a.required ? -1 : 1
    return a.title.localeCompare(b.title)
  })
}

/**
 * A default worth showing. Nebula writes `default: null` for facts that simply
 * declare the key with no value, so null/undefined are dropped; objects and
 * arrays are dropped too rather than rendered as `[object Object]` (no workflow
 * uses one today, and a fact row is too small to show structure usefully).
 */
function displayableDefault(
  value: unknown
): string | number | boolean | undefined {
  if (typeof value === 'string') return value.trim() === '' ? undefined : value
  if (typeof value === 'number' || typeof value === 'boolean') return value
  return undefined
}

/**
 * A fact the workflow's own YAML hard-codes to an entry in the org's fact
 * catalogue, in the same `{source, binding}` shape a variant's bindings use.
 *
 * `catalogue` is the only source a binding can name — a fixed value would be a
 * default, and instructions belong to a variant — so this is a lone-member union
 * rather than a mirror of `VariantFactBinding`, which reuses it.
 */
export interface FactCatalogueBinding {
  source: 'catalogue'
  binding: string
}

/**
 * One entry in Nebula's `facts_schema` map, as it arrives on the wire. Only
 * `description`/`required`/`default` are read here; `secret` and `env` are
 * declared so the duty-scoped fact type stays assignable to this one.
 */
export interface WorkflowFactSchema {
  /**
   * Human-readable name for the fact. Nebula always sends one, but it is
   * optional here the way `default`/`secret`/`env` are — a response predating
   * the field still parses. Read it through `factTitle`, never raw.
   */
  title?: string | null
  description: string | null
  required: boolean
  /**
   * The values this fact accepts, when the workflow declares it as an enum.
   * Null for a free-text fact. Nebula sends a duty's narrowed list under the
   * same key as the workflow's own, so this is the whole set either way.
   */
  enum?: string[] | null
  /**
   * Where the workflow itself reads this fact from, when its YAML binds one.
   * Null for the ordinary caller-supplied fact.
   *
   * **No deployed Nebula sends this yet.** Nova `main` has no catalogue concept
   * in its YAML fact model at all, so this parses as null on every response
   * today and the consumers that branch on it are inert. The emitter lives on
   * nova's unmerged `feat/chs-encounter-note` (`9da34d5c feat(facts): bind a
   * fact to the org fact catalogue`), which adds `catalogue` to
   * `ParsedWorkflowFactSchema` and emits `{source, binding}` from
   * `facts_schema`. Kept because the field is optional and costs nothing while
   * absent, and because that branch is this feature's own nova counterpart —
   * the alternative is deleting three pieces of code and restoring them
   * verbatim. Treat it as forward-compatible parsing, not current behaviour.
   *
   * A bound fact stays in the schema because it stays a declared input, and who
   * is calling decides what the binding means: a caller with a live subject
   * resolves it instead of asking for a value, one without — an operator
   * starting a session by hand — still has to supply it. On that branch a
   * variant may not rebind it either (`workflow_variants.py:149`); nova `main`
   * has no such rule, because it has nothing to refuse.
   */
  binding?: FactCatalogueBinding | null
  default?: unknown
  secret?: boolean
  env?: string | null
}

/** A plain object: narrows an unknown wire value before reading keys off it. */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * A fact's enum options, or null when it isn't one.
 *
 * Scalars are stringified the way a stored static binding is — a fact value is
 * opaque to Nebula and arrives back as text, so an enum of numbers has to
 * compare against the same text. An option that is neither a scalar nor
 * comparable is dropped rather than rendered as `[object Object]`; an empty
 * list reads as "not an enum", since a fact no value satisfies is not a choice.
 */
function parseFactEnum(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null
  const options = raw
    .filter(
      (option): option is string | number | boolean =>
        typeof option === 'string' ||
        typeof option === 'number' ||
        typeof option === 'boolean'
    )
    .map(String)
  return options.length ? options : null
}

/**
 * A fact's workflow-level binding, or null when it has none — which is every
 * fact on every response a deployed Nebula sends today; see
 * `WorkflowFactSchema.binding` for why the field is read at all.
 *
 * Once the emitter lands it arrives on any fact whose YAML sets `catalogue`, and
 * only ever with that source, so anything else is dropped rather than carried as
 * a binding no consumer knows how to read — as is a blank binding name, which
 * names no catalogue entry.
 */
function parseFactBinding(raw: unknown): FactCatalogueBinding | null {
  if (!isRecord(raw) || raw.source !== 'catalogue') return null
  const binding = typeof raw.binding === 'string' ? raw.binding.trim() : ''
  return binding ? { source: 'catalogue', binding } : null
}

/**
 * Narrow Nebula's `facts_schema` map. Facts with a non-object value are dropped;
 * `required`/`secret` default to false so a fact missing either key reads as
 * optional rather than silently required.
 *
 * Shared by the org- and duty-scoped listings — Nebula returns the same shape on
 * both endpoints, so they narrow it the same way.
 *
 * `__proto__` is dropped because this map enters React Query's structural-
 * sharing cache. When a refetch changes any sibling data, TanStack rebuilds the
 * map as a plain object with assignment; assigning that key reaches the inherited
 * setter, loses the fact, and changes property reads on the copy. Dropping the
 * unusable name here keeps the value safe through assignment-based copies too.
 * `Object.prototype` is never touched, so this is correctness rather than
 * prototype pollution.
 */
export function parseFactsSchema(
  raw: unknown
): Record<string, WorkflowFactSchema> | null {
  if (!isRecord(raw)) return null
  const facts: Record<string, WorkflowFactSchema> = {}
  for (const [name, value] of Object.entries(raw)) {
    if (!isRecord(value)) continue
    if (name === '__proto__') continue
    facts[name] = {
      title: typeof value.title === 'string' ? value.title : null,
      description:
        typeof value.description === 'string' ? value.description : null,
      required: value.required === true,
      enum: parseFactEnum(value.enum),
      binding: parseFactBinding(value.binding),
      secret: value.secret === true,
      default: value.default,
      env: typeof value.env === 'string' ? value.env : null,
    }
  }
  return facts
}

/**
 * Facts from Nebula's structured `facts_schema` map, in the same order and shape
 * `parseFacts` produces for the text column — so both sources render through the
 * same components. A fact with no description shows its name alone.
 */
export function factsFromSchema(
  schema: Record<string, WorkflowFactSchema> | null
): WorkflowFact[] {
  if (!schema) return []
  return sortFacts(
    Object.entries(schema).map(([id, fact]) => ({
      id,
      title: factTitle(id, fact.title),
      required: fact.required,
      description: fact.description ?? '',
      secret: fact.secret === true,
      // A secret fact's default is dropped here rather than at the component,
      // so the value never reaches the render model at all. Nebula is not a
      // reliable gate for this: on nova `main` `facts_schema` filters only
      // `if not fact.public`, and `secret`/`public` are independent fields with
      // no validator coupling them, so a fact declared `public: true` and
      // `secret: true` is emitted with its `default` intact.
      default:
        fact.secret === true ? undefined : displayableDefault(fact.default),
    }))
  )
}

export function parseFacts(facts: string): WorkflowFact[] {
  const trimmed = facts?.trim()
  if (!trimmed) return []

  if (MARKER_PATTERN.test(trimmed)) {
    const result: WorkflowFact[] = []
    for (const rawLine of trimmed.split('\n')) {
      const line = rawLine.trim()
      if (!line) continue
      const match = LINE_PATTERN.exec(line)
      if (match?.[1] && match[3]) {
        result.push({
          id: match[1],
          // The text column carries no titles, so every one is derived.
          title: defaultFactTitle(match[1]),
          required: match[2] !== 'optional',
          description: match[3].trim(),
        })
      }
    }
    return sortFacts(result)
  }

  // Legacy: treat every fact as required since markers are absent.
  const result: WorkflowFact[] = []
  for (const match of trimmed.matchAll(PARAGRAPH_PATTERN)) {
    const id = match[1]
    const description = match[2]
    if (id && description) {
      result.push({
        id,
        title: defaultFactTitle(id),
        required: true,
        description: description.trim(),
      })
    }
  }
  return sortFacts(result)
}
